import { useState, useEffect } from "react";

const ELIXIR_LOGO = "https://www.productelixir-ai.tech/assets/Elixirlogo_1769893492761-BCKEFavv.png";

const agents = [
  { id: "Policy-Agent-7", action: "Validate Entitlement", risk: "LOW", status: "APPROVED" },
  { id: "Audit-Bot-3", action: "Access Compliance Log", risk: "MEDIUM", status: "APPROVED" },
  { id: "Identity-Agent-9", action: "Modify Permission Set", risk: "HIGH", status: "ESCALATED" },
  { id: "Risk-AI-2", action: "Export Audit Data", risk: "CRITICAL", status: "BLOCKED" },
];

const stages = [
  { code: "AUTH", label: "Authenticate" },
  { code: "AUTHZ", label: "Authorize" },
  { code: "INTENT", label: "Validate Intent" },
  { code: "HITL", label: "Intervene" },
  { code: "ENFORCE", label: "Enforce" },
  { code: "AUDIT", label: "Audit" },
];

const getRiskBadge = (risk) => {
  switch (risk) {
    case "LOW": return "bg-emerald-500/20 text-emerald-400 border-emerald-500/40";
    case "MEDIUM": return "bg-amber-500/20 text-amber-400 border-amber-500/40";
    case "HIGH": return "bg-orange-500/20 text-orange-400 border-orange-500/40";
    case "CRITICAL": return "bg-red-500/20 text-red-400 border-red-500/40";
    default: return "bg-slate-500/20 text-slate-400 border-slate-500/40";
  }
};

const getStatusStyle = (status) => {
  switch (status) {
    case "APPROVED": return { dot: "bg-emerald-500", text: "text-emerald-400", glow: "shadow-emerald-500/60" };
    case "ESCALATED": return { dot: "bg-amber-500", text: "text-amber-400", glow: "shadow-amber-500/60" };
    case "BLOCKED": return { dot: "bg-red-500", text: "text-red-400", glow: "shadow-red-500/60" };
    default: return { dot: "bg-slate-500", text: "text-slate-400", glow: "shadow-slate-500/60" };
  }
};

export const InteractiveDashboard = () => {
  const [activeAgent, setActiveAgent] = useState(0);
  const [activeStage, setActiveStage] = useState(0);
  const [particlePos, setParticlePos] = useState(0);

  useEffect(() => {
    const agentInterval = setInterval(() => {
      setActiveAgent((prev) => (prev + 1) % agents.length);
    }, 2500);

    const stageInterval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % stages.length);
    }, 700);

    const particleInterval = setInterval(() => {
      setParticlePos((prev) => (prev + 2) % 100);
    }, 50);

    return () => {
      clearInterval(agentInterval);
      clearInterval(stageInterval);
      clearInterval(particleInterval);
    };
  }, []);

  const currentAgent = agents[activeAgent];

  return (
    <div className="relative" data-testid="interactive-dashboard">
      {/* Ambient glow */}
      <div className="absolute -inset-8 bg-gradient-to-br from-violet-600/30 via-indigo-500/20 to-violet-600/30 blur-3xl rounded-3xl animate-pulse" />
      
      {/* 3D Shadow underneath */}
      <div 
        className="absolute -bottom-6 left-[5%] right-[5%] h-12 bg-violet-900/40 blur-2xl rounded-[50%]"
      />
      
      {/* Main Card */}
      <div 
        className="relative rounded-2xl overflow-hidden border border-violet-500/30 bg-gradient-to-br from-slate-900 via-slate-900/98 to-slate-950"
        style={{
          boxShadow: `
            0 50px 100px -20px rgba(0, 0, 0, 0.7),
            0 30px 60px -10px rgba(0, 0, 0, 0.5),
            0 0 120px -30px rgba(139, 92, 246, 0.5),
            inset 0 1px 0 rgba(255, 255, 255, 0.1),
            inset 0 -1px 0 rgba(0, 0, 0, 0.3)
          `,
          transform: 'perspective(800px) rotateX(4deg) rotateY(-6deg) scale(1.02)',
          transformOrigin: 'center center'
        }}
      >
        {/* Glossy overlay */}
        <div 
          className="absolute inset-0 pointer-events-none z-20"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 50%)'
          }}
        />

        {/* Window Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-slate-800/60 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500 shadow-lg shadow-red-500/30" />
              <div className="w-3 h-3 rounded-full bg-amber-500 shadow-lg shadow-amber-500/30" />
              <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/30" />
            </div>
            <div className="flex items-center gap-2">
              <img src={ELIXIR_LOGO} alt="" className="w-5 h-5 object-contain" />
              <span className="text-sm font-semibold text-white">
                Elixir<span className="text-violet-400">ID</span>
                <span className="text-slate-500 font-normal ml-1.5">Dashboard</span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-lg shadow-emerald-500/50" />
            <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Live</span>
          </div>
        </div>

        {/* Stats Row */}
        <div className="flex items-center gap-6 px-4 py-2 bg-slate-900/60 border-b border-slate-800/50 text-xs text-slate-400">
          <span>Agents: <strong className="text-white">4 active</strong></span>
          <span>Decisions: 
            <strong className="text-emerald-400 ml-1">2 approved</strong>
            <strong className="text-amber-400 ml-1">1 escalated</strong>
            <strong className="text-red-400 ml-1">1 blocked</strong>
          </span>
        </div>

        {/* Main Content */}
        <div className="p-4">
          <div className="grid grid-cols-12 gap-4">
            
            {/* Left - AI Agents */}
            <div className="col-span-4">
              <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-2">AI Agents</div>
              <div className="space-y-1.5">
                {agents.map((agent, index) => {
                  const isActive = index === activeAgent;
                  
                  return (
                    <div 
                      key={agent.id}
                      className={`p-2.5 rounded-xl border transition-all duration-500 cursor-pointer ${
                        isActive 
                          ? 'bg-violet-500/10 border-violet-500/50 shadow-lg shadow-violet-500/20 scale-[1.02]' 
                          : 'bg-slate-800/40 border-slate-700/40 hover:border-slate-600/60'
                      }`}
                      onClick={() => setActiveAgent(index)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-white font-mono">{agent.id}</span>
                        <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border ${getRiskBadge(agent.risk)}`}>
                          {agent.risk}
                        </span>
                      </div>
                      <div className="text-[9px] text-slate-400">{agent.action}</div>
                      
                      {isActive && (
                        <div className="mt-2 h-0.5 bg-gradient-to-r from-violet-500 via-violet-400 to-transparent rounded-full" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Middle - Pipeline */}
            <div className="col-span-4 relative">
              <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-2 text-center">Pipeline</div>
              
              {/* Connection line with particle */}
              <div className="absolute left-1/2 top-8 bottom-2 w-0.5 -translate-x-1/2">
                <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/50 via-violet-500/50 to-slate-600/30 rounded-full" />
                <div 
                  className="absolute w-2.5 h-2.5 bg-violet-500 rounded-full shadow-lg shadow-violet-500/80 -left-1"
                  style={{ top: `${particlePos}%` }}
                />
              </div>

              <div className="space-y-1.5 relative z-10">
                {stages.map((stage, index) => {
                  const isActive = index === activeStage;
                  const isPast = index < activeStage;
                  
                  return (
                    <div 
                      key={stage.code}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all duration-300 ${
                        isActive
                          ? 'bg-violet-500/20 border border-violet-500/60 shadow-lg shadow-violet-500/30 scale-105'
                          : isPast
                          ? 'bg-emerald-500/10 border border-emerald-500/40'
                          : 'bg-slate-800/50 border border-slate-700/50'
                      }`}
                    >
                      <div className="relative">
                        <div className={`w-3 h-3 rounded-full transition-all ${
                          isActive 
                            ? 'bg-violet-500 shadow-lg shadow-violet-500/70' 
                            : isPast 
                            ? 'bg-emerald-500' 
                            : 'bg-slate-600'
                        }`} />
                        {isActive && (
                          <div className="absolute inset-0 w-3 h-3 rounded-full bg-violet-500 animate-ping opacity-75" />
                        )}
                      </div>
                      <div>
                        <div className={`text-[9px] font-bold font-mono ${
                          isActive ? 'text-violet-300' : isPast ? 'text-emerald-400' : 'text-slate-400'
                        }`}>
                          {stage.code}
                        </div>
                        <div className="text-[8px] text-slate-500">{stage.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right - Decisions */}
            <div className="col-span-4">
              <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-2">Decisions</div>
              <div className="space-y-1.5">
                {agents.map((agent, index) => {
                  const style = getStatusStyle(agent.status);
                  const isActive = index === activeAgent;
                  
                  return (
                    <div 
                      key={`decision-${agent.id}`}
                      className={`flex items-center justify-between p-2 rounded-xl transition-all duration-500 ${
                        isActive ? 'bg-slate-800/60' : ''
                      }`}
                    >
                      <span className="text-[9px] text-slate-500 font-mono truncate max-w-[60px]">
                        {agent.id.split('-')[0]}...
                      </span>
                      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-800/80 border border-slate-700/50 transition-all duration-500 ${
                        isActive ? 'scale-110 shadow-lg ' + style.glow : ''
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${style.dot} ${isActive ? 'animate-bounce' : ''}`} />
                        <span className={`text-[9px] font-bold ${style.text}`}>{agent.status}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 bg-slate-900/80 border-t border-slate-800/50">
          <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-slate-800/60 border border-slate-700/40">
            <div className="relative">
              <div className="w-2.5 h-2.5 rounded-full bg-violet-500" />
              <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-violet-500 animate-ping opacity-75" />
            </div>
            <span className="text-[10px] font-mono text-slate-300">
              <span className="font-bold text-white">{currentAgent.id}</span>
              <span className="text-slate-600 mx-1.5">→</span>
              <span className="text-slate-400">{currentAgent.action}</span>
              <span className="text-slate-600 mx-1.5">→</span>
              <span className={`font-bold ${
                currentAgent.status === 'APPROVED' ? 'text-emerald-400' :
                currentAgent.status === 'ESCALATED' ? 'text-amber-400' : 'text-red-400'
              }`}>{currentAgent.status}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
